import React from "react";
import useSWR, { mutate } from "swr";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/app/PageHeader";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { qcStatusLabel, qcDocTypeLabel, QC_STATUS } from "@/lib/labels";
import { QcCreateDialog } from "@/components/app/QcCreateDialog";
import { QcDetail, STATUS_BADGE } from "@/components/app/QcDetail";

const fetcher = (u) => api.get(u).then((r) => r.data);

function ReviewList({ items, selected, onSelect, clientMap }) {
  return (
    <div className="border border-zinc-200 bg-white rounded-md overflow-hidden">
      <div className="p-4 border-b border-zinc-200 text-[10px] uppercase tracking-[0.22em] text-zinc-500">Documents en relecture</div>
      <ul className="divide-y divide-zinc-100 max-h-[70vh] overflow-y-auto">
        {items.length === 0 && <li className="p-6 text-sm text-zinc-500" data-testid="qc-empty-list">Aucun document soumis.</li>}
        {items.map((it) => {
          const b = STATUS_BADGE[it.status] || STATUS_BADGE.a_relire;
          return (
            <li key={it.id}>
              <button data-testid={`qc-item-${it.id}`} onClick={() => onSelect(it)} className={`w-full text-left p-4 hover:bg-zinc-50 ${selected?.id === it.id ? "bg-zinc-50" : ""}`}>
                <div className="text-sm font-medium truncate">{it.title}</div>
                <div className="text-xs text-zinc-500 truncate">{qcDocTypeLabel(it.doc_type)} • {clientMap[it.client_id]?.name || "—"}</div>
                <div className="mt-1 flex items-center gap-2">
                  <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: b.bg, color: b.fg }}>{qcStatusLabel(it.status)}</span>
                  <span className="text-[11px] text-zinc-400">{new Date(it.created_at).toLocaleString("fr-FR")}</span>
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default function QualityControl() {
  const { reviewId } = useParams();
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = React.useState("tous");
  const listKey = statusFilter === "tous" ? "/qc/reviews" : `/qc/reviews?status=${statusFilter}`;
  const { data: items = [] } = useSWR(listKey, fetcher);
  const { data: clients = [] } = useSWR("/clients", fetcher);
  const [open, setOpen] = React.useState(false);
  const [selected, setSelected] = React.useState(null);
  const clientMap = Object.fromEntries(clients.map((c) => [c.id, c]));

  // La relecture sélectionnée est portée par l'URL (/controle-qualite/:reviewId) — survit au rechargement
  React.useEffect(() => {
    if (!reviewId) { setSelected(null); return; }
    api.get(`/qc/reviews/${reviewId}`).then((r) => setSelected(r.data)).catch(() => setSelected(null));
  }, [reviewId]);

  const selectReview = (it) => {
    // Toujours récupérer le détail complet (la liste exclut content_text et ai_report)
    api.get(`/qc/reviews/${it.id}`).then((r) => setSelected(r.data)).catch(() => {});
    navigate(`/controle-qualite/${it.id}`);
  };

  const refreshSelected = async () => {
    mutate(listKey);
    if (!selected) return;
    try {
      const r = await api.get(`/qc/reviews/${selected.id}`);
      setSelected(r.data);
    } catch { /* relecture supprimée */ setSelected(null); }
  };

  // Poll pendant la relecture IA
  const pollKey = selected && selected.ai_status === "en_generation" ? `/qc/reviews/${selected.id}` : null;
  useSWR(pollKey, fetcher, { refreshInterval: 2500, onSuccess: (d) => setSelected(d) });

  return (
    <div>
      <PageHeader
        eyebrow="Qualité"
        title="Relecture & Contrôle qualité"
        description="Soumission des documents de mission, relecture IA annotée (rédaction + conformité), checklists qualité par type de mission et validation par un expert."
        actions={<QcCreateDialog open={open} setOpen={setOpen} onCreated={(r) => { selectReview(r); mutate(listKey); }} />}
      />
      <div className="flex items-center gap-3 mb-4">
        <span className="text-xs uppercase tracking-[0.18em] text-zinc-500">Statut</span>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48" data-testid="qc-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="tous">Tous</SelectItem>
            {QC_STATUS.map((s) => (<SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>))}
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <ReviewList items={items} selected={selected} onSelect={selectReview} clientMap={clientMap} />
        <div className="lg:col-span-2">
          <QcDetail review={selected} onChanged={refreshSelected} />
        </div>
      </div>
    </div>
  );
}
